import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Package, Truck, Plus, Trash2, User, MapPin, ChevronDown, ChevronUp, Edit2, Check, X, Map as MapIcon } from "lucide-react";
import PincodeMapPicker from "@/components/pharmeasy/PincodeMapPicker";

/**
 * PincodeTripPlanner - Interactive pincode-based trip planning.
 * Props:
 *   pincodeSummary: [{ pincode, total, cod_count, prepaid_count, forward_count, return_count, total_value, shipment_ids }]
 *   availableRiders: [{ _id, name, phone }]
 *   onPlansReady: (plans) => void  -- called with final plans array
 *   plannedTrips / setPlannedTrips: state lifted to parent
 */
const PincodeTripPlanner = ({ pincodeSummary, availableRiders, plannedTrips, setPlannedTrips }) => {
  const [selectedPincodes, setSelectedPincodes] = useState({});  // { pincode: true }
  const [splitCounts, setSplitCounts] = useState({});  // { pincode: number } for partial selection
  const [selectedRider, setSelectedRider] = useState("");
  const [expandedTrip, setExpandedTrip] = useState(null);
  const [editingSplit, setEditingSplit] = useState(null);
  const [editSplitValue, setEditSplitValue] = useState("");
  const [mapPickerPincode, setMapPickerPincode] = useState(null); // pincode string when map is open
  const [selectedShipmentIds, setSelectedShipmentIds] = useState({}); // { pincode: [id1, id2, ...] }

  // Calculate remaining shipments per pincode after planned trips
  const remainingByPincode = useMemo(() => {
    const remaining = {};
    for (const p of pincodeSummary) {
      remaining[p.pincode] = { ...p, remaining: p.total };
    }
    for (const trip of plannedTrips) {
      for (const pincode of trip.pincodes) {
        if (remaining[pincode]) {
          const taken = trip.splits?.[pincode] || remaining[pincode].total;
          remaining[pincode].remaining = Math.max(0, remaining[pincode].remaining - taken);
        }
      }
    }
    return remaining;
  }, [pincodeSummary, plannedTrips]);

  // Filter to only show pincodes with remaining shipments
  const availablePincodes = useMemo(() => {
    return Object.values(remainingByPincode)
      .filter(p => p.remaining > 0)
      .sort((a, b) => b.remaining - a.remaining);
  }, [remainingByPincode]);

  // Riders already assigned to trips
  const usedRiderIds = useMemo(() => new Set(plannedTrips.map(t => t.rider_id).filter(Boolean)), [plannedTrips]);

  const totalPlannedShipments = useMemo(() => {
    return plannedTrips.reduce((sum, trip) => {
      const tripTotal = trip.pincodes.reduce((s, pc) => s + (trip.splits?.[pc] || remainingByPincode[pc]?.total || 0), 0);
      return sum + tripTotal;
    }, 0);
  }, [plannedTrips, remainingByPincode]);

  const totalShipments = pincodeSummary.reduce((s, p) => s + p.total, 0);

  const togglePincode = (pincode) => {
    setSelectedPincodes(prev => {
      const next = { ...prev };
      if (next[pincode]) {
        delete next[pincode];
        // Also clear split and selected IDs
        setSplitCounts(sc => { const n = { ...sc }; delete n[pincode]; return n; });
        setSelectedShipmentIds(si => { const n = { ...si }; delete n[pincode]; return n; });
      } else {
        next[pincode] = true;
      }
      return next;
    });
  };

  const selectAll = () => {
    const all = {};
    availablePincodes.forEach(p => { all[p.pincode] = true; });
    setSelectedPincodes(all);
  };

  const clearSelection = () => {
    setSelectedPincodes({});
    setSplitCounts({});
    setSelectedShipmentIds({});
  };

  const startEditSplit = (pincode, current) => {
    setEditingSplit(pincode);
    setEditSplitValue(String(current || remainingByPincode[pincode]?.remaining || ""));
  };

  const confirmSplit = (pincode) => {
    const val = parseInt(editSplitValue);
    const max = remainingByPincode[pincode]?.remaining || 0;
    if (!val || val <= 0) {
      toast.error("Enter a valid number");
      return;
    }
    if (val > max) {
      toast.error(`Max available: ${max}`);
      return;
    }
    if (val === max) {
      // Full selection — remove split
      setSplitCounts(prev => { const n = { ...prev }; delete n[pincode]; return n; });
    } else {
      setSplitCounts(prev => ({ ...prev, [pincode]: val }));
    }
    // Clear map-picked IDs when manually editing split
    setSelectedShipmentIds(prev => { const n = { ...prev }; delete n[pincode]; return n; });
    setEditingSplit(null);
  };

  const handleMapPickerConfirm = (pincode, ids) => {
    const max = remainingByPincode[pincode]?.remaining || 0;
    if (ids.length === 0) {
      // Deselect if 0 picked
      setSelectedPincodes(prev => { const n = { ...prev }; delete n[pincode]; return n; });
      setSplitCounts(prev => { const n = { ...prev }; delete n[pincode]; return n; });
      setSelectedShipmentIds(prev => { const n = { ...prev }; delete n[pincode]; return n; });
    } else {
      // Auto-select the pincode if not already
      setSelectedPincodes(prev => ({ ...prev, [pincode]: true }));
      if (ids.length < max) {
        setSplitCounts(prev => ({ ...prev, [pincode]: ids.length }));
      } else {
        setSplitCounts(prev => { const n = { ...prev }; delete n[pincode]; return n; });
      }
      setSelectedShipmentIds(prev => ({ ...prev, [pincode]: ids }));
    }
    setMapPickerPincode(null);
  };

  const addTrip = () => {
    const selected = Object.keys(selectedPincodes);
    if (selected.length === 0) {
      toast.error("Select at least one pincode");
      return;
    }
    if (!selectedRider) {
      toast.error("Select a rider for this trip");
      return;
    }
    if (usedRiderIds.has(selectedRider)) {
      toast.error("This rider is already assigned to another trip");
      return;
    }

    const splits = {};
    let shipmentCount = 0;
    let tripShipmentIds = [];
    for (const pc of selected) {
      const count = splitCounts[pc] || remainingByPincode[pc]?.remaining || 0;
      splits[pc] = count;
      shipmentCount += count;
      // Track specific IDs if map-picked
      if (selectedShipmentIds[pc]) {
        tripShipmentIds = tripShipmentIds.concat(selectedShipmentIds[pc]);
      }
    }

    const rider = availableRiders.find(r => r._id === selectedRider);

    const trip = {
      id: Date.now(),
      pincodes: selected,
      splits,
      rider_id: selectedRider,
      rider_name: rider?.name || "Unknown",
      shipment_count: shipmentCount,
      shipment_ids: tripShipmentIds.length > 0 ? tripShipmentIds : undefined,
    };

    setPlannedTrips(prev => [...prev, trip]);
    setSelectedPincodes({});
    setSplitCounts({});
    setSelectedRider("");
    setSelectedShipmentIds({});
    toast.success(`Trip ${plannedTrips.length + 1} planned: ${shipmentCount} shipments`);
  };

  const removeTrip = (tripId) => {
    setPlannedTrips(prev => prev.filter(t => t.id !== tripId));
  };

  const selectedCount = Object.keys(selectedPincodes).length;
  const currentSelectionShipments = Object.keys(selectedPincodes).reduce((sum, pc) => {
    return sum + (splitCounts[pc] || remainingByPincode[pc]?.remaining || 0);
  }, 0);

  return (
    <div className="space-y-4">
      {/* Summary Bar */}
      <div className="flex items-center justify-between bg-slate-50 rounded-lg p-4 border">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-slate-600">Total: <strong>{totalShipments}</strong></span>
          <span className="text-emerald-600">Planned: <strong>{totalPlannedShipments}</strong></span>
          <span className="text-orange-600">Remaining: <strong>{totalShipments - totalPlannedShipments}</strong></span>
          <span className="text-blue-600">Trips: <strong>{plannedTrips.length}</strong></span>
        </div>
        {totalPlannedShipments === totalShipments && (
          <Badge className="bg-emerald-100 text-emerald-700">All shipments planned</Badge>
        )}
      </div>

      {/* Planned Trips */}
      {plannedTrips.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-slate-700">Planned Trips ({plannedTrips.length})</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {plannedTrips.map((trip, idx) => (
              <div key={trip.id} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 cursor-pointer flex-1"
                    onClick={() => setExpandedTrip(expandedTrip === trip.id ? null : trip.id)}>
                    {expandedTrip === trip.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    <div>
                      <span className="font-medium text-blue-800">Trip {idx + 1}</span>
                      <span className="text-xs text-blue-600 ml-2">{trip.shipment_count} shipments</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                      <User className="w-3.5 h-3.5" />
                      <span>{trip.rider_name}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeTrip(trip.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                      data-testid={`remove-trip-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                {expandedTrip === trip.id && (
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <div className="flex flex-wrap gap-1">
                      {trip.pincodes.map(pc => (
                        <Badge key={pc} variant="outline" className="text-xs bg-white">
                          <MapPin className="w-3 h-3 mr-1" />{pc}: {trip.splits?.[pc] || "all"}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Pincodes Table */}
      {availablePincodes.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-700">
              Available Pincodes ({availablePincodes.length})
            </h4>
            <div className="flex gap-3">
              <Button variant="ghost" size="sm" onClick={selectAll} className="text-xs h-7">Select All</Button>
              {selectedCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearSelection} className="text-xs h-7 text-red-500">Clear</Button>
              )}
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-sm" data-testid="pincode-table">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap text-left w-10"></th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap text-left">Pincode</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap text-right">Remaining</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap text-center">Split</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap text-right">COD</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap text-right">Prepaid</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap text-right">Fwd</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap text-right">Rtn</th>
                  </tr>
                </thead>
                <tbody>
                  {availablePincodes.map((p) => {
                    const isSelected = !!selectedPincodes[p.pincode];
                    const splitVal = splitCounts[p.pincode];
                    return (
                      <tr key={p.pincode}
                        className={`border-t ${isSelected ? "bg-emerald-50" : "hover:bg-slate-50"} cursor-pointer`}
                        onClick={() => togglePincode(p.pincode)}>
                        <td className="px-4 py-3 align-middle">
                          <Checkbox checked={isSelected} onCheckedChange={() => togglePincode(p.pincode)}
                            data-testid={`pincode-check-${p.pincode}`} />
                        </td>
                        <td className="px-4 py-3 align-middle font-medium">{p.pincode}</td>
                        <td className="px-4 py-3 align-middle text-right font-medium">{p.remaining}</td>
                        <td className="px-4 py-3 align-middle text-center" onClick={(e) => e.stopPropagation()}>
                          {isSelected && (
                            editingSplit === p.pincode ? (
                              <div className="flex items-center gap-1 justify-center">
                                <Input
                                  type="number"
                                  value={editSplitValue}
                                  onChange={(e) => setEditSplitValue(e.target.value)}
                                  className="w-16 h-7 text-xs text-center"
                                  min={1}
                                  max={p.remaining}
                                  autoFocus
                                  onKeyDown={(e) => { if (e.key === "Enter") confirmSplit(p.pincode); if (e.key === "Escape") setEditingSplit(null); }}
                                />
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-emerald-600" onClick={() => confirmSplit(p.pincode)}>
                                  <Check className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => setEditingSplit(null)}>
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 justify-center">
                                <Button variant="ghost" size="sm"
                                  className="h-6 text-xs text-blue-600 hover:text-blue-800"
                                  onClick={() => startEditSplit(p.pincode, splitVal)}
                                  data-testid={`split-btn-${p.pincode}`}>
                                  {splitVal ? <span className="font-medium text-orange-600">{splitVal}/{p.remaining}</span> : <Edit2 className="w-3 h-3" />}
                                </Button>
                                {p.shipments?.some(s => s.coords) && (
                                  <Button variant="ghost" size="sm"
                                    className="h-6 px-1.5 text-xs text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                                    onClick={() => setMapPickerPincode(p.pincode)}
                                    title="Pick from Map"
                                    data-testid={`map-pick-btn-${p.pincode}`}>
                                    <MapIcon className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            )
                          )}
                        </td>
                        <td className="px-4 py-3 align-middle text-right text-sm text-slate-500">{p.cod_count}</td>
                        <td className="px-4 py-3 align-middle text-right text-sm text-slate-500">{p.prepaid_count}</td>
                        <td className="px-4 py-3 align-middle text-right text-sm text-slate-500">{p.forward_count}</td>
                        <td className="px-4 py-3 align-middle text-right text-sm text-slate-500">{p.return_count}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Add Trip Controls */}
      {availablePincodes.length > 0 && (
        <div className="flex items-center gap-4 bg-slate-50 rounded-lg p-4 border">
          <div className="flex items-center gap-3 flex-1">
            <Select value={selectedRider} onValueChange={setSelectedRider}>
              <SelectTrigger className="w-56" data-testid="pincode-rider-select">
                <SelectValue placeholder="Select rider" />
              </SelectTrigger>
              <SelectContent>
                {availableRiders.map(r => (
                  <SelectItem key={r._id} value={r._id} disabled={usedRiderIds.has(r._id)}>
                    <span className={usedRiderIds.has(r._id) ? "text-slate-400 line-through" : ""}>
                      {r.name} {r.phone ? `(${r.phone})` : ""}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedCount > 0 && (
              <span className="text-sm text-slate-600">
                {selectedCount} pincodes, <strong>{currentSelectionShipments}</strong> shipments
              </span>
            )}
          </div>
          <Button onClick={addTrip} disabled={selectedCount === 0 || !selectedRider}
            style={{ backgroundColor: selectedCount > 0 && selectedRider ? "#1B8C3D" : "#9CA3AF" }}
            data-testid="add-trip-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Trip
          </Button>
        </div>
      )}

      {/* All planned message */}
      {availablePincodes.length === 0 && plannedTrips.length > 0 && (
        <div className="text-center py-6 bg-emerald-50 rounded-lg border border-emerald-200">
          <Truck className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
          <p className="text-emerald-700 font-medium">All shipments have been planned into {plannedTrips.length} trips</p>
          <p className="text-sm text-emerald-600 mt-1">Click "Create Trips" to optimize routes and finalize</p>
        </div>
      )}

      {/* Map Picker Dialog */}
      {mapPickerPincode && (() => {
        const pData = remainingByPincode[mapPickerPincode];
        const allShipments = pData?.shipments || pincodeSummary.find(p => p.pincode === mapPickerPincode)?.shipments || [];
        // Filter to only remaining shipments (not used in planned trips)
        const usedIds = new Set();
        for (const trip of plannedTrips) {
          if (trip.shipment_ids) trip.shipment_ids.forEach(id => usedIds.add(id));
          else {
            // For trips without specific IDs, mark all shipments of those pincodes as used
            for (const pc of trip.pincodes) {
              const pcShipments = pincodeSummary.find(p => p.pincode === pc)?.shipments || [];
              const limit = trip.splits?.[pc] || pcShipments.length;
              pcShipments.slice(0, limit).forEach(s => usedIds.add(s.id));
            }
          }
        }
        const available = allShipments.filter(s => !usedIds.has(s.id));
        return (
          <PincodeMapPicker
            open={true}
            onClose={() => setMapPickerPincode(null)}
            pincode={mapPickerPincode}
            shipments={available}
            initialSelected={selectedShipmentIds[mapPickerPincode] || available.map(s => s.id)}
            onConfirm={(ids) => handleMapPickerConfirm(mapPickerPincode, ids)}
          />
        );
      })()}
    </div>
  );
};

export default PincodeTripPlanner;
